import { useMemo } from 'react'

import { ArticleMedia } from '@/components/common/Article'
import { ErrorBlock } from '@/components/common/ErrorBlock'
import { StyledContent } from '@/components/common/StyledContent'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { usePage } from '@/components/providers/PageProvider'
import { ParsedContentProvider, useParsedContent } from '@/components/providers/ParsedContentProvider'
import { useScrollspyRefs } from '@/components/providers/ScrollspyProvider'
import { MEDIAVINE_INLINE_CSS_CLASS } from '@/defs/ad'
import { isIconType } from '@/defs/icon'
import { createDynamicComponent } from '@/utils/factoryUtil'

import styles from './GuideContent.module.scss'
import { GuideContentSection } from './GuideContentSection/GuideContentSection'

const ArticleReport = createDynamicComponent({
  loader: () => import('@/components/common/Article'),
  name: 'ArticleReport',
})

export const GuideContent = () => {
  const { page } = usePage()
  const { content, guide, isPreview, slug, title } = page?.article || {}

  const sections = useMemo(
    () => guide?.flatMap((g) => g.sections.map(({ content, slug }) => ({ content, slug }))),
    [guide],
  )

  if (!content) return <ErrorBlock />

  return (
    <ParsedContentProvider
      articleSlug={slug}
      articleTitle={title}
      content={content}
      getReport={isPreview}
      sections={sections}
    >
      <GuideContentInner />
    </ParsedContentProvider>
  )
}

const GuideContentInner = () => {
  const { page } = usePage()
  const sectionRefs = useScrollspyRefs()
  const { article } = page || {}
  const { guide } = article || {}
  const { content, reports, sections = {} } = useParsedContent()

  return (
    <div className={styles.container}>
      <div className={styles.info}>
        <ArticleMedia article={article} />
      </div>
      <div className={styles.content}>
        {reports && <ArticleReport reports={reports} />}
        <StyledContent className={MEDIAVINE_INLINE_CSS_CLASS}>{content}</StyledContent>
        {guide?.map((g, i) => (
          <div key={i}>
            <h2 className={styles.chapter}>
              <IconWithLabel iconBefore={isIconType(g.icon) ? g.icon : 'angleRight'}>{g.chapter}</IconWithLabel>
            </h2>
            {g.sections.map((s, j) => (
              <GuideContentSection
                content={sections[s.slug]}
                key={j}
                number={j + 1}
                section={s}
                sectionRef={sectionRefs[s.slug]}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
